<template>
  <div>
    <div class="row">
      <div class="col-lg-12">
        <div class="m-b-md">
          <div v-if="project.archived" style="background-color: #FFDFDDFF">
            <span class="large-font line-through">{{ project.name || 'Project' }}</span>
            <i class="fa fa-exclamation-triangle m-l-sm red fa-lg" v-tooltip="'This project has been deleted'"></i>
          </div>
          <div v-else class="flex-space-between">
            <router-link :to="{path: `/task-manager/projects/${project.id}`}">
              <h2 v-if="canEdit">
                <a-edit-txt type="text" :text="project.name" @input="updateProperty($event, 'name')" close-on-save="true"></a-edit-txt>
              </h2>
              <h2 v-else>{{ project.name }}</h2>
            </router-link>
            <button v-if="canEdit" class="btn btn-xs btn-danger pull-right m-r-xs" @click="deleteProject">Delete project</button>
          </div>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-lg-6">
        <dl class="row mb-0" v-if="project.slug">
          <div class="col-sm-4 text-sm-right">
            <dt v-tooltip="'Unique project identifier'">Slug</dt>
          </div>
          <div class="col-sm-8 text-sm-left">
            <dd class="mb-1">
              <span>{{ project.slug }}</span>
            </dd>
          </div>
        </dl>
        <dl class="row mb-0">
          <div class="col-sm-4 text-sm-right">
            <dt>Status:</dt>
          </div>
          <div class="col-sm-8 text-sm-left">
            <a-edit-txt
              v-if="canEdit" type="select"
              :text="project.status || '/'"
              :selected-value="project.status || null" :options="statuses"
              select-label="label" select-value="index"
              @input="updateProperty($event, 'status')"
              close-on-save='true'>
            </a-edit-txt>
            <dd v-else class="mb-1"><span class="label label-primary">{{ project.status || '/' }}</span></dd>
          </div>
        </dl>
        <dl class="row mb-0">
          <div class="col-sm-4 text-sm-right">
            <dt>Departments:</dt>
          </div>
          <div class="col-sm-8 text-sm-left">
            <dd class="mb-1">
              <div id="departments-data">
                <span v-for="(dep, i) in project.departments" :key="project.id + '-dep-' + i" class="label label-success m-r-xs" style="display: inline-block;">
                  {{ dep }}
                </span>
                <a v-if="canEdit" class="pull-right" @click="toggleEdit('departments-data')"><i class="fa fa-edit"></i></a>
              </div>
              <div id="departments-edit" style="display: none">
                <span class="col-lg-10 col-md-10 col-sm-10 col-xs-10">
                  <v-select
                  :options="departments"
                  label="label" index="index" multiple
                  v-model="project.departments"
                ></v-select>
                </span>
                <span class="col-lg-2 col-md-2 col-sm-2 col-xs-2 flex-space-between">
                  <button class="btn btn-xs btn-success m-r-xs" v-tooltip="'Save changes'" @click="updateProperty(project.departments, 'departments'); toggleEdit('departments-edit')"><i class="fa fa-save"></i></button>
                  <button class="btn btn-xs btn-danger" v-tooltip="'Cancel edit'" @click="toggleEdit('departments-edit')"><i class="fa fa-cancel"></i></button>
                </span>
              </div>
            </dd>
          </div>
        </dl>
        <dl class="row mb-0">
          <div class="col-sm-4 text-sm-right">
            <dt>Client:</dt>
          </div>
          <div class="col-sm-8 text-sm-left">
            <dd class="mb-1">
              <div id="client-data">
                <span class="label">{{ project.client ? project.client.name : '/' }}</span>
                <a v-if="canEdit" class="pull-right" @click="toggleEdit('client-data')"><i class="fa fa-edit"></i></a>
              </div>
              <div id="client-edit" style="display: none">
                <span class="col-lg-10 col-md-10 col-sm-10 col-xs-10">
                  <v-select
                    :options="f.clients"
                    label="name" index="id"
                    v-model="project.client"
                  ></v-select>
                </span>
                <span class="col-lg-2 col-md-2 col-sm-2 col-xs-2 flex-space-between">
                  <button class="btn btn-xs btn-success m-r-xs" v-tooltip="'Save changes'" @click="updateProperty(project.client, 'client'); toggleEdit('client-edit')"><i class="fa fa-save"></i></button>
                  <button class="btn btn-xs btn-danger" v-tooltip="'Cancel edit'" @click="toggleEdit('client-edit')"><i class="fa fa-cancel"></i></button>
                </span>
              </div>
            </dd>
          </div>
        </dl>
        <dl class="row mb-0">
          <div class="col-sm-4 text-sm-right">
            <dt>Tags:</dt>
          </div>
          <div class="col-sm-8 text-sm-left">
            <dd class="mb-1">
              <div id="tags-data">
                <span v-for="(tag, i) in project.tags" :key="project.id + '-tag-' + i" class="label label-success m-r-xs" style="display: inline-block;">
                  {{ tag }}
                </span>
                <a v-if="canEdit" class="pull-right" @click="toggleEdit('tags-data')"><i class="fa fa-edit"></i></a>
              </div>
              <div id="tags-edit" style="display: none">
                <span class="col-lg-10 col-md-10 col-sm-10 col-xs-10">
                  <v-select
                    multiple taggable push-tags
                    v-model="project.tags"
                  ></v-select>
                </span>
                <span class="col-lg-2 col-md-2 col-sm-2 col-xs-2 flex-space-between">
                  <button class="btn btn-xs btn-success m-r-xs" v-tooltip="'Save changes'" @click="updateProperty(project.tags, 'tags'); toggleEdit('tags-edit')"><i class="fa fa-save"></i></button>
                  <button class="btn btn-xs btn-danger" v-tooltip="'Cancel edit'" @click="toggleEdit('tags-edit')"><i class="fa fa-cancel"></i></button>
                </span>
              </div>
            </dd>
          </div>
        </dl>
        <dl class="row mb-0">
          <div class="col-sm-4 text-sm-right">
            <dt>Files:</dt>
          </div>
          <div class="col-sm-8 text-sm-left">
            <dd class="well">
              <file-interface v-if="project.id" :data="project" type="projects" @reloadData="reloadProject" :upload-disabled="!canEdit"></file-interface>
            </dd>
          </div>
        </dl>
      </div>
      <div class="col-lg-6">
        <dl class="row mb-0">
            <div class="col-sm-4 text-sm-right">
                <dt>Target Date:</dt>
            </div>
            <div class="col-sm-8 text-sm-left">
              <a-edit-txt
                v-if="canEdit"
                type="date"
                :text="project.targetDate"
                @input="updateProperty($event, 'targetDate')"
                close-on-save="true"
              ></a-edit-txt>
              <dd v-else class="mb-1">{{ project.targetDate | aTime() }} ({{ project.targetDate | aTime('from') }})</dd>
            </div>
        </dl>
        <dl class="row mb-0">
            <div class="col-sm-4 text-sm-right">
              <dt>Participants:</dt>
            </div>
            <div class="col-sm-8 text-sm-left">
                <dd class="project-people mb-1">
                  <div id="participants-data">
                    <div style="display: block;">
                      <span v-for="user in project.participants" :key="'participants-' + user.id" class="label label-primary m-r-xs" style="display: inline-block;">
                        {{ user.username }}
                      </span>
                    </div>
                    <a v-if="canEdit" class="pull-right" @click="toggleEdit('participants-data')"><i class="fa fa-edit"></i></a>
                  </div>
                  <div id="participants-edit" style="display: none">
                    <span class="col-lg-10 col-md-10 col-sm-10 col-xs-10">
                      <v-select
                        multiple :options="users"
                        v-model="projectParticipants"
                        label="username" index="id"
                      >
                      </v-select>
                    </span>
                    <span class="col-lg-2 col-md-2 col-sm-2 col-xs-2 flex-space-between">
                      <button class="btn btn-xs btn-success m-r-xs" v-tooltip="'Save changes'" @click="updateProperty(projectParticipants, 'participants'); toggleEdit('participants-edit')"><i class="fa fa-save"></i></button>
                      <button class="btn btn-xs btn-danger" v-tooltip="'Cancel edit'" @click="toggleEdit('participants-edit')"><i class="fa fa-cancel"></i></button>
                    </span>
                  </div>
                </dd>
            </div>
        </dl>
        <dl class="row mb-1">
            <div class="col-sm-4 text-sm-right">
              <dt>Group:</dt>
            </div>
            <div class="col-sm-8 text-sm-left">
                <dd class="mb-1">
                  <div id="group-data">
                    <div>
                      <span class="label">{{ project.group ? project.group.name : '/' }}</span>
                    </div>
                    <a v-if="canEdit" class="pull-right" @click="toggleEdit('group-data')"><i class="fa fa-edit"></i></a>
                  </div>
                  <div id="group-edit" style="display: none">
                    <span class="col-lg-10 col-md-10 col-sm-10 col-xs-10">
                      <v-select
                        :options="f.groups"
                        label="name" index="id"
                        v-model="project.group"
                      ></v-select>
                    </span>
                    <span class="col-lg-2 col-md-2 col-sm-2 col-xs-2 flex-space-between">
                      <button class="btn btn-xs btn-success m-r-xs" v-tooltip="'Save changes'" @click="updateProperty(project.group, 'group'); toggleEdit('group-edit')"><i class="fa fa-save"></i></button>
                      <button class="btn btn-xs btn-danger" v-tooltip="'Cancel edit'" @click="toggleEdit('group-edit')"><i class="fa fa-cancel"></i></button>
                    </span>
                  </div>
                </dd>
            </div>
        </dl>
        <div class="row mb-0">
          <div class="col-sm-12 text-sm-left">
            <h4>Description</h4>
            {{ project.description }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import countries from '../../../../../../../static/countries.json'
import { mapGetters } from 'vuex'
import FileInterface from '../../../../../Helpers/Files/FileInterface.vue'
import { storeToRefs } from 'pinia'
import { useDataStore } from '../../../../../../stores/DataStore'
import { useProjectStore } from '../../../../../../stores/ProjectStore'

export default {
  setup () {
    const ps = useProjectStore()
    const data = useDataStore()
    data.loadDepartments()
    const statuses = data.taskStatuses
    const { departments } = storeToRefs(data)
    if (!departments.length) data.loadDataStructures(['departments', 'projectmanager-task-statuses'])
    return { ps, departments, statuses }
  },
  data () {
    return {
      countries,
      projectParticipants: []
    }
  },
  props: ['taskView', 'task', 'project'],
  components: {
    fileInterface: FileInterface
  },
  computed: {
    ...mapGetters({
      user: 'getUser',
      dataStructure: 'getData',
      f: 'getFilters',
      users: 'getUsers'
    }),
    canEdit () {
      if (this.taskView) return false
      if (!this.project.client) return this.user.master
      return this.user.master || this.user.clients.map(el => el.id).includes(this.project.client.id)
    }
  },
  async mounted () {
    if (!this.users.length) {
      await this.$store.dispatch('loadUsers')
    }
    if (!this.f.clients) {
      await this.$store.dispatch('loadFilters')
    }
  },
  watch: {
    project () {
      this.projectParticipants = [...this.project.participants.map(p => p.id)]
    }
  },
  methods: {
    async reloadProject () {
      await this.ps.loadProject(this.project.id)
    },
    async updateProperty (val, key) {
      let data = {}
      data[key] = val
      try {
        await this.ps.patchProject(this.project.id, data)
        this.$toastr.success(`Project ${key} updated`, 'Success')
      } catch (error) {
        this.$toastr.error('Update Failed', error)
      }
    },
    async deleteProject () {
      if (confirm(`Are you sure you want to delete ${this.project.name}?`)) {
        try {
          await this.ps.deleteProject(this.project.id)
          this.$toastr.success('You will be redirected to projects page', 'Project deleted!')
          this.$router.push('/task-manager/projects')
        } catch (error) {
          this.$toastr.error('Failed to delete project', error)
        }
      }
    },
    toggleEdit (id) {
      const idToShow = id.includes('data') ? id.replace('data', 'edit') : id.replace('edit', 'data')
      this.hideData(id)
      this.showData(idToShow)
    },
    showData (id) {
      document.getElementById(id).style.display = 'block'
    },
    hideData (id) {
      document.getElementById(id).style.display = 'none'
    }
  }
}
</script>
<style scoped>
.large-font {
  font-size: large;
}
.line-through {
  text-decoration: line-through;
}
.flex-space-between {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>



// WEBPACK FOOTER //
// src/components/Pages/TaskManager/Projects/Single/Header/Header.vue